import {
  CRITICAL_AUTOMATED_SOURCES,
  EXTERNAL_AGENT_MESSAGE_SOURCE,
  type AgentMode,
  type ChatMessage,
  type RunnerMode,
  type TaskContext,
} from "@project/shared";

export function findLastAgentMessageIndex(history: ChatMessage[]): number {
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].role === "assistant") return i;
  }
  return -1;
}

/**
 * Messages newer than the relaunch cursor. The cursor is the id of the most
 * recent message in the chat at the end of the agent's last successful turn.
 *
 * - Cursor inside the visible window → everything after that index is new.
 * - Cursor older than the window (fell off the 20-msg tail) → the entire
 *   window is newer than what the agent has seen, so treat all of it as new.
 *   This is the bug the cursor fixes: urgent user messages outside the window
 *   were previously invisible to `findLastAgentMessageIndex` and the agent
 *   reported "no new instructions."
 */
export function messagesAfterCursor(
  history: ChatMessage[],
  lastSeenMessageId: string | null | undefined,
): ChatMessage[] {
  if (!lastSeenMessageId) return history;
  const idx = history.findIndex((m) => m.id === lastSeenMessageId);
  return idx === -1 ? history : history.slice(idx + 1);
}

/**
 * The messages a relaunch prompt treats as "since your last run" — cursor-based
 * when the cursor is set, else everything after the agent's own last message.
 * Unlike the slices rendered into the prompt this keeps every role: critical
 * automated posts land as `role: "system"`.
 */
export function relaunchMessageBatch(context: TaskContext): ChatMessage[] {
  if (context.lastSeenMessageId) {
    return messagesAfterCursor(context.chatHistory, context.lastSeenMessageId);
  }
  return context.chatHistory.slice(findLastAgentMessageIndex(context.chatHistory) + 1);
}

/** Mirrors the `isCodeReview` derivation in `buildInstructions`. */
export function isCodeReviewRun(mode: RunnerMode | undefined, agentMode?: AgentMode | null): boolean {
  return mode === "code-review" || (mode !== "pm" && agentMode === "review");
}

function isActionableRelaunchMessage(m: ChatMessage): boolean {
  if (m.source && CRITICAL_AUTOMATED_SOURCES.has(m.source)) return true;
  return m.role === "user" && m.source !== EXTERNAL_AGENT_MESSAGE_SOURCE;
}

/**
 * A builder woken while its PR is under review must not take an implement turn
 * unless there is genuinely something to implement.
 *
 * The platform wakes a task's primary workspace purely to HOST a code review
 * (the `wake_deferred` shape in `ensureReviewCompute`). That wake looked
 * identical to a feedback relaunch to the prompt builder — any new chat since
 * the last turn (an external worker's status post, a teammate's note) read as
 * feedback — so the builder was handed "implement your updates and open a PR",
 * i.e. authorization to push to a branch that is mid-review. It burned a
 * phantom work turn.
 *
 * Hold instead, unless the new batch contains something real to act on:
 *
 * - a critical automated source — a request-changes verdict (`review_trigger`)
 *   or a CI failure (`ci_failure`) IS actionable, and keeps today's implement
 *   behavior verbatim; or
 * - a human directive — any other `role: "user"` message. The exception is a
 *   post made by someone's Conveyor-MCP agent (`EXTERNAL_AGENT_MESSAGE_SOURCE`),
 *   which is a status update from another worker, not an instruction. Those are
 *   exactly what read as "feedback" and triggered the phantom turn.
 *
 * Keyed on runner mode + task status + message source, never on session role —
 * same-pod review children are readers while dedicated review writers exist, so
 * role is not a usable contract (see `.claude/rules/code-review.md`, the
 * 2026-07-07 regression note).
 */
export function isReviewHostHold(
  context: TaskContext,
  newMessages: ChatMessage[],
  isCodeReview: boolean,
): boolean {
  // A code-review run woken on a ReviewPR task is doing its actual job.
  if (isCodeReview) return false;
  if (context.status !== "ReviewPR" || !context.githubPRUrl) return false;
  return !newMessages.some((m) => isActionableRelaunchMessage(m));
}

/**
 * The hold prompt: surface what arrived, then wait. The host still connects and
 * renews liveness — which is what delivers the parked review spawn
 * (`deliverPendingReviewSpawn` rides renewal, not the agent's turn) — it just
 * does not go do work.
 */
export function buildReviewHostHoldParts(
  context: TaskContext,
  newMessages: ChatMessage[],
): string[] {
  const parts = [
    `You were relaunched while this task is in review, but nothing here asks you to change the code.`,
    `Most likely this workspace was woken to HOST the code review session, not to work.`,
    `Do NOT take a work turn: do not re-review your own diff, re-run gates, commit, or push. The PR is mid-review and moving the branch now would pull it out from under the reviewer.`,
    `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
  ];
  if (newMessages.length > 0) {
    parts.push(
      `\nNew messages since your last run:`,
      ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
    );
  }
  parts.push(
    `\nReview these messages and wait for the team to provide instructions before taking action.`,
    `If the review requests changes, or CI fails, you will be woken again with that feedback — act then, not now.`,
  );
  if (context.githubPRUrl) {
    parts.push(`An existing PR is open at ${context.githubPRUrl}. Do not create a new PR.`);
  }
  return parts;
}
